import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { environment } from '../../../environments/environment';

/**
 * First-party (Buky-owned) GA4 + Facebook Pixel tracking for the app itself
 * (app.buky.me). It uses the SAME measurement/pixel IDs as the promo sites
 * (buky.me / buky.us) so that "ad -> visit -> registration" attribution is
 * linked across domains: gtag.js automatically reads the `_gl` linker param
 * from the URL, so no extra cross-domain code is needed here.
 *
 * This is intentionally SEPARATE from `ConversionTrackingService`, which loads
 * each tenant's OWN googleTagId/facebookPixelId on the public booking widget
 * (/portal). Mixing the two would cross-contaminate Buky's analytics with
 * tenant pixels, so this service never initializes on /portal routes.
 *
 * IDs come from the build-time `environment` (empty in dev/staging/tenvito ->
 * the service becomes a no-op there, keeping prod analytics clean).
 */
@Injectable({ providedIn: 'root' })
export class AppAnalyticsService {
  private document = inject(DOCUMENT);
  private router = inject(Router);

  private readonly gaMeasurementId: string = environment.gaMeasurementId ?? '';
  private readonly fbPixelId: string = environment.fbPixelId ?? '';

  private initialized = false;
  private signUpTracked = false;

  /** Wire up route-change PageView tracking. Call once from AppComponent. */
  init(): void {
    if (!this.enabled) return;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => {
        // Skip the public booking widget: tenants load their own pixels there.
        if (e.urlAfterRedirects.startsWith('/portal')) return;
        this.ensureInitialized();
        this.trackPageView(e.urlAfterRedirects);
      });
  }

  trackPageView(path: string): void {
    // `send_to` (GA4) and `trackSingle` (FB) scope events to OUR IDs only, so
    // they never leak into a tenant's property/pixel if both this service and
    // the per-tenant ConversionTrackingService happen to be live in the same
    // tab (e.g. an owner previewing their own /portal widget while logged in).
    this.gtag('event', 'page_view', { page_path: path, send_to: this.gaMeasurementId });
    this.fbq('trackSingle', this.fbPixelId, 'PageView');
  }

  /**
   * Fire the registration conversion EXACTLY ONCE, only after the backend has
   * confirmed the account was created. The `signUpTracked` guard protects
   * against duplicate firing on SPA re-render.
   * @param method how the user registered: 'email' | 'google' | ...
   */
  trackSignUp(method: string): void {
    if (!this.enabled || this.signUpTracked) return;
    this.signUpTracked = true;
    this.ensureInitialized();
    this.gtag('event', 'sign_up', { method, send_to: this.gaMeasurementId });
    this.fbq('trackSingle', this.fbPixelId, 'CompleteRegistration', { value: 0.1, currency: 'EUR' });
  }

  /**
   * TODO: wire this up once a paid plan / activation flow exists. Do NOT call
   * with placeholder data — only on a confirmed purchase with real values
   * (e.g. from the billing/subscription success callback).
   */
  trackPurchase(transactionId: string, value: number, currency: string): void {
    if (!this.enabled) return;
    this.ensureInitialized();
    this.gtag('event', 'purchase', { transaction_id: transactionId, value, currency, send_to: this.gaMeasurementId });
    this.fbq('trackSingle', this.fbPixelId, 'Purchase', { value, currency });
  }

  private get enabled(): boolean {
    return !!(this.gaMeasurementId || this.fbPixelId);
  }

  private ensureInitialized(): void {
    if (this.initialized || !this.enabled) return;
    this.initialized = true;
    if (this.gaMeasurementId) this.injectGoogleTag(this.gaMeasurementId);
    if (this.fbPixelId) this.injectFacebookPixel(this.fbPixelId);
  }

  private injectGoogleTag(tagId: string): void {
    const win = this.document.defaultView as any;
    if (!win.gtag) {
      const script = this.document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
      this.document.head.appendChild(script);

      win.dataLayer = win.dataLayer || [];
      win.gtag = function () { win.dataLayer.push(arguments); };
      win.gtag('js', new Date());
    }
    // send_page_view: false — we fire page_view manually on each route change.
    win.gtag('config', tagId, { send_page_view: false });
  }

  private injectFacebookPixel(pixelId: string): void {
    const win = this.document.defaultView as any;
    if (!win.fbq) {
      const n: any = (win.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!win._fbq) win._fbq = n;
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];

      const script = this.document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      this.document.head.appendChild(script);
    }
    win.fbq('init', pixelId);
  }

  private gtag(...args: any[]): void {
    try {
      const win = this.document.defaultView as any;
      if (this.gaMeasurementId && win.gtag) win.gtag(...args);
    } catch { /* ad blocker or script not loaded */ }
  }

  private fbq(...args: any[]): void {
    try {
      const win = this.document.defaultView as any;
      if (this.fbPixelId && win.fbq) win.fbq(...args);
    } catch { /* ad blocker or script not loaded */ }
  }
}
